'use client';

import { Settings } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { appConfig } from '@/app/config/appConfig';
import { DifficultySelectModal } from '@/components/shared/DifficultySelectModal';
import { PageLoader } from '@/components/shared/PageLoader';
import { SkipConfirmModal } from '@/components/shared/SkipConfirmModal';
import { Alert } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Toast, ToastClose, ToastDescription, ToastTitle } from '@/components/ui/toast';
import { useGameSession } from '@/features/game/hooks/useGameSession';
import { useSoundSystem } from '@/features/game/hooks/useSoundSystem';
import { useStageContent } from '@/features/stage-content/hooks/useStageContent';
import { useToast } from '@/hooks/useToast';
import { HostBridge } from '@/lib/hostBridge';
import { moveTile } from '@/lib/sliding-puzzle/board';
import { GameStatus, type DemoLevel } from '@/types/sliding-puzzle';
import mandalaImage from '@micro-screens/assets/mandala-vector.png';
import {
  GameFailureScreen,
  GameInstructionsScreen,
  GameInstructionsSkeleton,
  GameSuccessScreen,
  STAGE_THEME_COLORS,
  type StageId,
} from '@micro-screens/src';

import { gameEngine } from '../engine/game-engine';

import '../styles/game.css';
import DemoFlow from './DemoFlow';
import GameBoard from './GameBoard';
import GameEditor from './GameEditor';
import GameTimer from './GameTimer';
import { LivesIndicator } from './LivesIndicator';
import MemorizeOverlay from './MemorizeOverlay';
import RevealOverlay from './RevealOverlay.tsx';
import RoundProgressDots from './RoundProgressDots';
import SoundSettings from './SoundSettings';

type AppScreen = 'start' | 'demo' | 'game';

const GATHER_START_DELAY_MS = 750;
const GATHER_TRANSITION_MS = 550;
const GATHER_SETTLE_MS = 120;
const SCATTER_TRANSITION_MS = 550;
const SCATTER_SETTLE_MS = 200;
const LEVEL_COMPLETE_BURST_MS = 1450;

function buildSolvedPieces(gridX: number, gridY: number): number[] {
  const total = gridX * gridY;
  return Array.from({ length: total }, (_, index) => (index === total - 1 ? -1 : index));
}

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${String(r)}, ${String(g)}, ${String(b)}`;
}

export default function GameContainer() {
  const { t } = useTranslation();
  const { toast: pushToast, toasts, dismiss } = useToast();

  // ── URL params (kept for HostBridge compatibility) ──────────────────────
  const _urlParams = new URLSearchParams(window.location.search);
  const urlStageId = _urlParams.get('stageId') ?? '';
  const urlGameSessionId = _urlParams.get('gameSessionId') ?? '';
  const urlTimer = _urlParams.get('timer') === 'true';
  const urlTimeLimit = _urlParams.has('timeLimit') ? Number(_urlParams.get('timeLimit')) : 60;

  const game = useGameSession({
    stageId: urlStageId,
    gameSessionId: urlGameSessionId,
    key: '',
  });
  const sound = useSoundSystem();
  const [screen, setScreen] = useState<AppScreen>('start');
  const [isInitReady, setIsInitReady] = useState(false);
  const [demoLevels, setDemoLevels] = useState<DemoLevel[]>([]);
  const [demoPrefetchStatus, setDemoPrefetchStatus] = useState<
    'idle' | 'available' | 'unavailable'
  >('idle');
  const initAttemptedRef = useRef(false);
  const [skeletonMinDurationMet, setSkeletonMinDurationMet] = useState(false);
  const [startErrorDismissed, setStartErrorDismissed] = useState(false);
  const [runtimeErrorDismissed, setRuntimeErrorDismissed] = useState(false);
  const [showDifficultyModal, setShowDifficultyModal] = useState(false);
  const [showSkipConfirmModal, setShowSkipConfirmModal] = useState(false);
  const prevPhaseRef = useRef(game.phase);
  const levelCompleteBurstTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [levelCompleteBurst, setLevelCompleteBurst] = useState(false);
  const [stopAnimation, setStopAnimation] = useState(false);
  const stopAnimationRef = useRef(false);
  const [introShufflePieces, setIntroShufflePieces] = useState<number[] | null>(null);
  const [gatherToCenter, setGatherToCenter] = useState(false);
  const [showBlankImage, setShowBlankImage] = useState(false);

  useEffect(() => {
    stopAnimationRef.current = stopAnimation;
  }, [stopAnimation]);

  useEffect(() => {
    if (stopAnimation) {
      setLevelCompleteBurst(false);
      if (levelCompleteBurstTimerRef.current) {
        clearTimeout(levelCompleteBurstTimerRef.current);
        levelCompleteBurstTimerRef.current = null;
      }
    }
  }, [stopAnimation]);

  // Play success sound when the reveal starts (board solved)
  useEffect(() => {
    if (game.phase === 'revealing' && prevPhaseRef.current !== 'revealing') {
      sound.playSuccess();
      if (levelCompleteBurstTimerRef.current) {
        clearTimeout(levelCompleteBurstTimerRef.current);
      }
      setLevelCompleteBurst(false);
      if (!stopAnimationRef.current) {
        requestAnimationFrame(() => {
          setLevelCompleteBurst(true);
        });
        levelCompleteBurstTimerRef.current = setTimeout(() => {
          setLevelCompleteBurst(false);
          levelCompleteBurstTimerRef.current = null;
        }, LEVEL_COMPLETE_BURST_MS);
      }
    }
    prevPhaseRef.current = game.phase;
  }, [game.phase, sound]);

  useEffect(() => {
    return () => {
      if (levelCompleteBurstTimerRef.current) {
        clearTimeout(levelCompleteBurstTimerRef.current);
      }
    };
  }, []);

  // Start background music the first time the game leaves idle/error
  const bgStartedRef = useRef(false);
  useEffect(() => {
    if (!bgStartedRef.current && game.phase !== 'idle' && game.phase !== 'error') {
      bgStartedRef.current = true;
      sound.startBackground();
    }
  }, [game.phase, sound]);

  // Tile click with sound
  const handleTileClickWithSound = useCallback(
    (slot: number) => {
      if (game.phase === 'playing' && game.boardState) {
        const { pieces } = game.boardState;
        const columns = game.boardState.board.gridSize.x;
        if (pieces[slot] !== -1) {
          const result = moveTile(pieces, slot, columns);
          if (result.moved) {
            sound.playWhoosh();
          } else {
            sound.playWrongStep();
          }
        }
      }
      game.handleTileClick(slot);
    },
    [game, sound],
  );

  // Auto-solve with tap sound
  const autoSolveWithSound = useCallback(() => {
    sound.playTap();
    game.autoSolve();
  }, [game, sound]);

  // ── 300 ms minimum skeleton timer — fires on mount ───────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setSkeletonMinDurationMet(true);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // ── Init: load demo data from local engine (no API calls) ────────────────
  useEffect(() => {
    if (initAttemptedRef.current) return;
    initAttemptedRef.current = true;

    // Load demo levels from the local game engine
    if (gameEngine.isDemoEnabled()) {
      const levels = gameEngine.getDemoLevels();
      setDemoLevels(levels);
      setDemoPrefetchStatus('available');
    } else {
      setDemoPrefetchStatus('unavailable');
    }

    setIsInitReady(true);
  }, []);

  const { contentByStage, isLoading: isStageContentLoading } = useStageContent(urlStageId);
  const stageNo = appConfig.game.stageNo as StageId;
  const theme = STAGE_THEME_COLORS[stageNo];
  const themeVars = useMemo(() => ({
    '--game-accent': theme.resultAccent,
    '--game-accent-rgb': hexToRgb(theme.resultAccent),
    '--game-eclipse': theme.eclipse,
    '--game-eclipse-rgb': hexToRgb(theme.eclipse),
    '--game-bg': theme.background,
    '--game-bg-rgb': hexToRgb(theme.background),
  } as CSSProperties), [theme]);

  useEffect(() => {
    Object.entries(themeVars).forEach(([property, value]) => {
      document.documentElement.style.setProperty(property, value as string);
    });
  }, [themeVars]);

  const { phase, boardState, remainingMs, scoreboard, finalStatus, error, lives } = game;
  const startScreenError = error;
  const shouldShowStartError = Boolean(startScreenError) && !startErrorDismissed;
  const shouldShowRuntimeError = Boolean(error) && !runtimeErrorDismissed;

  // ── Timer enabled check ──────────────────────────────────────────────────
  const isTimerEnabled = urlTimer;

  useEffect(() => {
    if (phase !== 'shuffle-animation' || !boardState) {
      if (phase !== 'shuffle-animation') {
        setIntroShufflePieces(null);
        setGatherToCenter(false);
        setShowBlankImage(false);
      }
      return;
    }

    const { gridSize } = boardState.board;
    const solvedPieces = buildSolvedPieces(gridSize.x, gridSize.y);
    const timers: Array<ReturnType<typeof setTimeout>> = [];

    setIntroShufflePieces(solvedPieces);
    setGatherToCenter(false);
    setShowBlankImage(true);

    const scatterAt = GATHER_START_DELAY_MS + GATHER_TRANSITION_MS + GATHER_SETTLE_MS;
    const completeAt = scatterAt + SCATTER_TRANSITION_MS + SCATTER_SETTLE_MS;

    timers.push(
      setTimeout(() => {
        setGatherToCenter(true);
      }, GATHER_START_DELAY_MS),
      setTimeout(() => {
        setGatherToCenter(false);
        setIntroShufflePieces(boardState.pieces);
        setShowBlankImage(false);
      }, scatterAt),
      setTimeout(game.onShuffleAnimationComplete, completeAt),
    );

    return () => {
      timers.forEach((timer) => {
        clearTimeout(timer);
      });
    };
  }, [boardState, game.onShuffleAnimationComplete, phase]);

  useEffect(() => {
    setStartErrorDismissed(false);
  }, [startScreenError]);

  useEffect(() => {
    setRuntimeErrorDismissed(false);
  }, [error]);

  // ── Render gate: show skeleton while init runs ───────────────────────────
  if (!isInitReady) {
    return (
      <div className="relative min-h-[100dvh]" style={themeVars}>
        <PageLoader />
      </div>
    );
  }

  // ── Demo screen ──────────────────────────────────────────────────────────
  if (screen === 'demo') {
    return (
      <DemoFlow
        stage={stageNo}
        levels={demoLevels}
        onComplete={() => {
          setScreen('start');
        }}
        sound={sound}
        stopAnimation={stopAnimation}
        onStopAnimationToggle={() => {
          setStopAnimation((v) => !v);
        }}
      />
    );
  }

  // ── Skeleton — shown while stage content is still resolving ───────────────
  if (
    screen === 'start' &&
    (phase === 'idle' || phase === 'error') &&
    (isStageContentLoading || !skeletonMinDurationMet)
  ) {
    return (
      <div className="relative min-h-[100dvh]" style={themeVars}>
        <GameInstructionsSkeleton stage={stageNo} />
      </div>
    );
  }

  // ── Start / instructions screen ──────────────────────────────────────────
  if (screen === 'start' && (phase === 'idle' || phase === 'error')) {
    return (
      <div className="relative min-h-[100dvh]" style={themeVars}>
        {/* Puzzle editor trigger — floats above the instructions screen */}
        <div className="absolute top-6 right-6 z-[60] flex items-center gap-2">
          <Dialog>
            <DialogContent
              className="bg-[#0f172a] border-white/10 text-white max-w-md backdrop-blur-2xl max-h-[90vh] overflow-y-auto"
              onPointerDownOutside={(e) => {
                e.preventDefault();
              }}
              onEscapeKeyDown={(e) => {
                e.preventDefault();
              }}
            >
              <DialogHeader>
                <DialogTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
                  <Settings className="h-5 w-5 text-sky-400" />
                  {t('game.puzzleEditor')}
                </DialogTitle>
              </DialogHeader>
              <GameEditor />
            </DialogContent>
          </Dialog>
        </div>

        {/* Game error alert */}
        {shouldShowStartError && (
          <div className="fixed left-1/2 bottom-[max(18px,env(safe-area-inset-bottom))] z-[70] w-[calc(100vw-32px)] max-w-[520px] -translate-x-1/2 px-4">
            <Alert
              variant="error"
              title={t('game.connectionLost')}
              description={startScreenError ?? undefined}
              onClose={() => {
                setStartErrorDismissed(true);
              }}
            />
          </div>
        )}

        <GameInstructionsScreen
          stage={stageNo}
          contentByStage={contentByStage}
          hideLearnHowToPlay={demoPrefetchStatus === 'unavailable'}
          onPlayNow={() => {
            setShowDifficultyModal(true);
          }}
          onLearnHowToPlay={() => {
            setScreen('demo');
          }}
        />

        <DifficultySelectModal
          isOpen={showDifficultyModal}
          onClose={() => {
            setShowDifficultyModal(false);
          }}
          onSelect={(d) => {
            setShowDifficultyModal(false);
            sound.startBackground();
            game.startGame({ enableTimer: urlTimer, timeLimit: urlTimeLimit, difficulty: d });
          }}
        />
      </div>
    );
  }

  // ── Loading / calculating result ─────────────────────────────────────────
  if (phase === 'loading' || phase === 'calculating_result') {
    return (
      <main
        className={`puzzle-shell${levelCompleteBurst && !stopAnimation ? ' puzzle-shell--level-complete' : ''} flex flex-col h-screen overflow-hidden select-none`}
      >
        <div className="background-layer" style={{ backgroundImage: `url(${mandalaImage})` }} />
        {levelCompleteBurst && !stopAnimation && (
          <div className="puzzle-level-complete-burst" aria-hidden="true" />
        )}
        <div
          className="puzzle-shell__top-glow"
          style={{
            background: `linear-gradient(${theme.eclipse}, ${theme.eclipse}52)`,
            boxShadow: `0 0 108px ${theme.eclipse}b8`,
          }}
        />
        <div className="relative z-[2] h-[100dvh] flex items-center justify-center">
          <div className="puzzle-loading">
            <div className="puzzle-loading__spinner" />
            <p className="puzzle-loading__text">
              {phase === 'calculating_result' ? t('game.calculatingResult') : t('game.loading')}
            </p>
          </div>
        </div>
      </main>
    );
  }

  // ── Result screen ────────────────────────────────────────────────────────
  if ((phase === 'completed' || phase === 'expired') && finalStatus !== null) {
    const isSuccess = finalStatus === GameStatus.ENDED;
    const completedGames =
      scoreboard?.rounds.filter((r) => r.solved === true).length ?? 0;
    const totalGames = boardState?.totalRounds ?? scoreboard?.rounds.length ?? 0;

    const handleContinue = async () => {
      const urlToken = _urlParams.get('token') ?? '';
      if (urlToken) {
        await HostBridge.continue(
          {
            score: scoreboard?.totalScore ?? 0,
            stageId: urlStageId,
            gameSessionId: urlGameSessionId,
            completedRounds: completedGames,
            totalRounds: totalGames,
          },
          {
            message: t('results.exitCloseTabHint'),
            showToast: (message) => {
              pushToast({ title: message });
            },
            exitDelayMs: 0,
          },
        );
        return;
      }
      // No host bridge — reset local state to play again
      game.flushAll();
    };

    if (isSuccess) {
      return (
        <GameSuccessScreen
          stage={stageNo}
          score={scoreboard?.totalScore ?? 0}
          completedGames={completedGames}
          totalGames={totalGames}
          onContinue={() => {
            void handleContinue();
          }}
        />
      );
    }

    return (
      <>
        <GameFailureScreen
          stage={stageNo}
          score={scoreboard?.totalScore ?? 0}
          completedGames={completedGames}
          totalGames={totalGames}
          onContinue={() => {
            void handleContinue();
          }}
        />
      </>
    );
  }

  // ── Active game ──────────────────────────────────────────────────────────
  const board = boardState?.board;
  const pieces = boardState?.pieces;
  const totalRounds = boardState?.totalRounds ?? 0;

  const isMemorizing = phase === 'memorizing';
  const isShuffleAnimation = phase === 'shuffle-animation';
  const isBoardClearing = phase === 'board-clearing' || phase === 'loading-next';
  const isRevealing = phase === 'revealing';
  const solvedPieces = board ? buildSolvedPieces(board.gridSize.x, board.gridSize.y) : null;
  const boardDisplayPieces =
    isMemorizing && solvedPieces
      ? solvedPieces
      : isShuffleAnimation
        ? (introShufflePieces ?? solvedPieces ?? pieces)
        : pieces;

  // Header shows "Level N + timer" only when actively playing
  const isGameActive = board != null && phase !== 'idle' && phase !== 'error';

  return (
    <main
      className={`puzzle-shell${levelCompleteBurst && !stopAnimation ? ' puzzle-shell--level-complete' : ''} flex flex-col h-screen overflow-hidden select-none`}
      style={themeVars}
    >
      <div className="background-layer" style={{ backgroundImage: `url(${mandalaImage})` }} />
      {levelCompleteBurst && !stopAnimation && (
        <div className="puzzle-level-complete-burst" aria-hidden="true" />
      )}
      <div
        className="puzzle-shell__top-glow"
        style={{
          background: `linear-gradient(${theme.eclipse}, ${theme.eclipse}52)`,
          boxShadow: `0 0 108px ${theme.eclipse}b8`,
        }}
      />

      <div className="relative z-[2] h-[100dvh] flex flex-col justify-between pt-[30px] pb-[15px] lg:py-[32px]">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <header className="puzzle-header">
          {isGameActive ? (
            <span className="puzzle-header__level font-semibold text-[16px] md:text-[18px] tracking-[1px] tracking-[0.16px] text-white whitespace-nowrap">
              {levelCompleteBurst
                ? t('game.levelComplete')
                : t('game.level', { number: board.roundNumber })}
            </span>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            {isGameActive && <LivesIndicator lives={lives} />}
            {!isTimerEnabled && (
              <button
                type="button"
                className={`sp-skip-btn${isGameActive && phase === 'playing' && lives > 0 ? '' : ' sp-skip-btn--hidden'}`}
                onClick={() => {
                  setShowSkipConfirmModal(true);
                }}
                disabled={lives <= 0}
              >
                {t('game.skipLevel')}
              </button>
            )}
            {isGameActive && isTimerEnabled && <GameTimer remainingMs={remainingMs} />}
            <SoundSettings
              sound={sound}
              stopAnimation={stopAnimation}
              onStopAnimationToggle={() => {
                setStopAnimation((v) => !v);
              }}
              showSkip={isTimerEnabled && isGameActive && phase === 'playing' && lives > 0}
              onSkip={() => {
                setShowSkipConfirmModal(true);
              }}
            />
          </div>
        </header>

        {/* ── Playfield ──────────────────────────────────────────────── */}
        <div className="puzzle-playfield flex-1 relative">
          {/* Board */}
          {board && pieces && (
            <div className="absolute inset-0 flex items-center justify-center px-4 pb-16">
              <div
                style={{
                  width: '100%',
                  maxWidth: '420px',
                  aspectRatio: `${String(board.gridSize.x)} / ${String(board.gridSize.y)}`,
                  maxHeight: '100%',
                }}
              >
                <div className="puzzle-board-card" style={{ height: '100%' }}>
                  <GameBoard
                    board={board}
                    pieces={boardDisplayPieces ?? pieces}
                    disabled={isMemorizing || isShuffleAnimation || isBoardClearing || isRevealing}
                    isSolved={isBoardClearing || isRevealing}
                    shuffleMode={isShuffleAnimation}
                    gatherToCenter={gatherToCenter}
                    showBlankImage={showBlankImage}
                    revealMode={isMemorizing}
                    onTileClick={handleTileClickWithSound}
                  />

                  {isMemorizing && (
                    <MemorizeOverlay
                      fullImageUrl={board.fullImageUrl}
                      gridX={board.gridSize.x}
                      gridY={board.gridSize.y}
                      displayTime={board.displayTime}
                      showImage={false}
                      onComplete={game.onMemorizeComplete}
                    />
                  )}

                  {isRevealing && (
                    <RevealOverlay
                      fullImageUrl={board.fullImageUrl}
                      gridX={board.gridSize.x}
                      gridY={board.gridSize.y}
                      onComplete={game.onRevealComplete}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Round progress dots */}
          {isGameActive && totalRounds > 0 && (
            <RoundProgressDots currentRound={board.roundNumber} totalRounds={totalRounds} />
          )}
        </div>

        {/* Dev: auto-solve */}
        {appConfig.features.devtools && phase === 'playing' && (
          <div className="puzzle-bottom-bar">
            <button type="button" className="puzzle-btn-ghost" onClick={autoSolveWithSound}>
              Auto Solve
            </button>
          </div>
        )}
      </div>

      {/* Runtime game error alert */}
      {shouldShowRuntimeError && (
        <div className="fixed left-1/2 bottom-[max(18px,env(safe-area-inset-bottom))] z-[100] w-[calc(100vw-32px)] max-w-[520px] -translate-x-1/2 px-4">
          <Alert
            variant="error"
            title={t('game.error')}
            description={error ?? undefined}
            onClose={() => {
              setRuntimeErrorDismissed(true);
            }}
          />
        </div>
      )}

      <SkipConfirmModal
        isOpen={showSkipConfirmModal}
        onCancel={() => {
          setShowSkipConfirmModal(false);
        }}
        onConfirm={() => {
          setShowSkipConfirmModal(false);
          game.skipLevel();
        }}
      />

      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2 p-4">
        {toasts.map(({ id, title, description, open, variant }) => (
          <Toast
            key={id}
            open={open}
            variant={variant}
            onOpenChange={(nextOpen) => {
              if (!nextOpen) dismiss(id);
            }}
          >
            <div className="grid gap-1">
              {title ? <ToastTitle>{title}</ToastTitle> : null}
              {description ? <ToastDescription>{description}</ToastDescription> : null}
            </div>
            <ToastClose
              onClick={() => {
                dismiss(id);
              }}
            />
          </Toast>
        ))}
      </div>
    </main>
  );
}
